/**
 * Slice 21d — Toast when a new Delivered notify appears for the signed-in owner.
 */
import { useEffect, useRef } from "react";
import { useOwnerNotifications } from "@/hooks/useOwnerNotifications";
import { toast } from "@/hooks/use-toast";
import {
  RESTOCK_DELIVERED_TYPE,
  RESTOCK_DELIVERED_TOAST_SUB,
  restockDeliveredToastTitle,
} from "@/lib/restockDeliveredNotify";

const SEEN_KEY = "stravo.restock_delivered_toast_seen";

function loadSeen(): Set<string> {
  try {
    const raw = localStorage.getItem(SEEN_KEY);
    if (!raw) return new Set();
    const arr = JSON.parse(raw) as string[];
    return new Set(Array.isArray(arr) ? arr : []);
  } catch {
    return new Set();
  }
}

function saveSeen(ids: Set<string>) {
  try {
    localStorage.setItem(SEEN_KEY, JSON.stringify([...ids].slice(-80)));
  } catch {
    /* ignore */
  }
}

function propertyFromMessage(message: string): string {
  const first = (message || "").split("\n")[0] || "";
  const before = first.split(" · ")[0]?.trim();
  return before || "Property";
}

export function DeliveredNotifyToaster() {
  const { data: notifications = [] } = useOwnerNotifications(20);
  const primed = useRef(false);

  useEffect(() => {
    const delivered = notifications.filter((n) => n.type === RESTOCK_DELIVERED_TYPE && !n.is_read);
    const seen = loadSeen();

    if (!primed.current) {
      // First load: don't toast historical unread; only mark seen.
      for (const n of delivered) seen.add(n.id);
      saveSeen(seen);
      primed.current = true;
      return;
    }

    let changed = false;
    for (const n of delivered) {
      if (seen.has(n.id)) continue;
      seen.add(n.id);
      changed = true;
      toast({
        title: restockDeliveredToastTitle(propertyFromMessage(n.message)),
        description: RESTOCK_DELIVERED_TOAST_SUB,
      });
    }
    if (changed) saveSeen(seen);
  }, [notifications]);

  return null;
}
